import { formatCreditBreakdownEntry } from '@frontend/api-client';
import type { Credit } from '@frontend/api-types';
import { capitalize, sortBy } from '@frontend/duck-tape';
import { Button, Text, XStack, YStack } from '@frontend/web-react';
import { usePricingTerminology } from '@frontend/web/context';

type CreditsBreakdownCardProps = {
  credits?: Credit;
  onViewHistory: () => void;
};

export const CreditsBreakdownCard = ({ credits, onViewHistory }: CreditsBreakdownCardProps) => {
  const { pricingTerminology, showCredits } = usePricingTerminology();

  if (credits === undefined) {
    return null;
  }

  // Sort active allocations by expiration date in reverse order (earliest expiring first)
  const sortedAllocations = sortBy(credits.activeAllocations || [], (allocation) =>
    new Date(allocation.expiration).getTime(),
  );

  return (
    <YStack className="bg-trueWhite border-1 border-borderPrimary rounded-lg p-md gap-lg">
      <XStack className="items-start justify-between">
        <YStack className="gap-xs">
          <Text color="secondary" type="p-md">
            {pricingTerminology.balance_title}
          </Text>
          {showCredits ? (
            <Text color="primary" type="h1">
              {credits.availableCredits}
            </Text>
          ) : null}
        </YStack>
        <Button
          label={pricingTerminology.history_title}
          leftIconName="IconHistory"
          onClick={onViewHistory}
          variant="secondary"
        />
      </XStack>

      {showCredits && sortedAllocations.length > 0 ? (
        <YStack className="gap-sm">
          <Text color="secondary" type="p-md">
            {`${capitalize(pricingTerminology.unit_term)} breakdown`}
          </Text>
          {sortedAllocations.map((allocation) => {
            const remainingCredits = allocation.amount - allocation.amountUsed;
            if (remainingCredits <= 0) return null;

            const formattedEntry = formatCreditBreakdownEntry(
              allocation.creditType,
              remainingCredits,
              allocation.expiration,
            );

            return (
              <XStack className="items-center justify-between" key={allocation.id}>
                <Text color="primary" style={{ flex: 1 }} type="p-sm">
                  {formattedEntry.creditType}
                </Text>
                <XStack className="gap-sm">
                  <XStack className="gap-xs">
                    <Text color="primary" style={{ flex: 1, textAlign: 'right' }} type="p-sm">
                      {formattedEntry.paddedNumber}
                    </Text>
                    <Text color="primary" style={{ flex: 1, textAlign: 'right' }} type="p-sm">
                      {formattedEntry.paddedCreditWord}
                    </Text>
                  </XStack>
                  {formattedEntry.expiration ? (
                    <Text color="tertiary" style={{ flex: 1, textAlign: 'right' }} type="p-sm">
                      {formattedEntry.expiration}
                    </Text>
                  ) : null}
                </XStack>
              </XStack>
            );
          })}
        </YStack>
      ) : null}
    </YStack>
  );
};
